import { observable, action, configure, computed, makeObservable } from 'mobx';
import { persist } from 'mobx-persist';

import { log } from 'shared-pages/services';
import { parseISO, differenceInSeconds } from 'date-fns';
import { jwtToken, userIsAdmin } from 'shared-pages/shared/authorizations';
import { decodeJwtToken, type JwtToken, type UserRolesIds } from 'shared-pages/shared/jwt';
import { UserTypeValues } from 'shared-pages/shared/constants';
import { getMotionUIStoreFromRef } from 'store-ref';

configure({ enforceActions: 'always' }); // strict mode

type UserLogin = {
  expirationDate: string;
  jwt: string | null;
  userName: string;
  userFullName: string;
  userFirstName?: string;
  userLastName?: string;
  userId?: number;
  userMustChangePassword?: boolean;
};

const initialUserLogin: UserLogin = {
  expirationDate: '',
  jwt: null,
  userName: '',
  userFullName: '',
  userFirstName: '',
  userLastName: '',
  userId: 0,
  userMustChangePassword: false,
};

//------------------------------------------------------
// *    --- Functions ---
//

// *    --- Store Class ---
//
export class AppStore {
  @observable initialized = false;
  @persist('object') @observable userLogin: UserLogin = { ...initialUserLogin };
  @persist @observable user = '';
  @observable devMode = false;
  @persist @observable dbAlias = '';
  @persist @observable pageSize = 10;
  @observable jwtExpireLeft = 0;
  @action setPageSize(n: number): void {
    if (n > 0) this.pageSize = n;
  }

  @observable curDate = new Date(); // Ημέρα Ζυγίσεων

  /**
   * Global Busy Logic
   */
  @observable private busyCnt = 0;
  @observable private busyReg = false;
  @observable private progressCnt = 0;
  @computed get busy(): boolean {
    return this.busyCnt > 0 || this.busyReg;
  }
  @computed get progress(): number {
    return Math.ceil(this.progressCnt);
  }
  @action setDevelopmentMode(on: boolean): void {
    this.devMode = on;
    log.log(`[AppStore] Development mode ${on ? 'ENABLED' : 'DISABLED'}`);
    // Στο dev mode τα field tooltips ανοίγουν δυναμικά, αλλά όταν κλείνει
    // επιστρέφουν στη βασική ρύθμιση από τα env variables.
    const fieldTooltipEnabledByEnv =
      process.env.VITE_APP_SHOW_FIELD_TOOLTIP === 'true' || process.env.REACT_APP_SHOW_FIELD_TOOLTIP === 'true';
    getMotionUIStoreFromRef()?.setShowFieldTooltip(on || fieldTooltipEnabledByEnv);
  }
  @action toggleDevelopmentMode(): void {
    this.setDevelopmentMode(!this.devMode);
  }
  @action regBusy(value: boolean): void {
    //log.log('[AppStore]: regBusy +', value);
    this.busyReg = value;
  }
  @action setBusy(): void {
    this.busyCnt++;
    //log.log('[AppStore]: Busy +', this.busyCnt);
  }
  @action setProgress(value: number): void {
    this.progressCnt = value;
    //log.log('[AppStore]: Busy +', this.busyCnt);
  }
  @action clearBusy(): void {
    this.busyCnt--;
    //log.log('[AppStore]: Busy -', this.busyCnt);
  }
  @action clearProgress(): void {
    this.progressCnt = 0;
    //log.log('[AppStore]: Busy -', this.busyCnt);
  }

  // *
  // * --------------- Getters -------------------------------
  @computed get isLogIn(): boolean {
    console.log('[app-store] isLogin', JSON.stringify(!!this.userLogin?.jwt));
    return !!this.userLogin?.jwt;
  }

  buildContext = function (): { jwtToken?: JwtToken } {
    if (!this.userLogin.jwt) return {};
    return { jwtToken: decodeJwtToken(this.userLogin.jwt) };
  };

  @computed
  get isAdmin(): boolean {
    const context = this.buildContext();
    return userIsAdmin(context);
  }

  @computed get userRolesIds(): UserRolesIds[] {
    if (!this.userLogin.jwt) return [];
    const jwt = decodeJwtToken(this.userLogin.jwt);
    return jwt.userRolesIds ?? [];
  }

  @computed get usertypeId(): number {
    const context = this.buildContext();
    const jwt = jwtToken(context);
    return jwt?.userTypeId ?? 0;
  }

  @computed get isPlatformOwner(): boolean {
    return this.usertypeId === UserTypeValues.PlatformOwner;
  }

  @computed get isCollector(): boolean {
    return this.usertypeId === UserTypeValues.Collector;
  }

  @computed get isCollectorDriver(): boolean {
    return this.usertypeId === UserTypeValues.CollectorDriver;
  }

  @computed get isCollectionPoint(): boolean {
    return this.usertypeId === UserTypeValues.CollectionPoint;
  }

  @computed get isWasteReceptionPoint(): boolean {
    return this.usertypeId === UserTypeValues.WasteReceptionPoint;
  }

  @computed get isContributor(): boolean {
    return this.usertypeId === UserTypeValues.Contributor;
  }

  @computed get associatedId(): number {
    if (!this.userLogin.jwt) {
      console.log('$$$ ############ no jwt');
      return 0;
    }
    const jwt = decodeJwtToken(this.userLogin.jwt);

    return jwt.associatedId ?? 0;
  }

  @computed get associatedId2(): number {
    if (!this.userLogin.jwt) {
      console.log('$$$ ############ no jwt');
      return 0;
    }
    const jwt = decodeJwtToken(this.userLogin.jwt);
    return jwt.associatedId2 ?? 0;
  }

  @computed get userFullName(): string {
    if (this.userLogin.userFullName) return this.userLogin.userFullName;
    const first = this.userLogin.userFirstName ?? '';
    const last = this.userLogin.userLastName ?? '';
    return `${first} ${last}`.trim();
  }

  @computed get calcJwtExpireLeft(): number {
    if (!this.isLogIn) return 0;
    return differenceInSeconds(parseISO(this.userLogin.expirationDate), new Date());
  }
  // * --------------- Methods -------------------------------

  @action login(user: string, userData: Partial<UserLogin>): void {
    this.user = user;
    this.userLogin = { ...initialUserLogin, ...userData };

    // Session marker (used to detect same-tab session)
    const dt = new Date(Date.now());
    sessionStorage.setItem('date', dt.toLocaleDateString() + ' ' + dt.toLocaleTimeString());

    // Explicit persist to localStorage (mobx-persist reaction may not fire with observable accessors)
    persistAppStoreToStorage(this);
  }

  @action logOut(): void {
    this.user = '';
    this.dbAlias = '';
    this.userLogin = { ...initialUserLogin };
  }

  @action setCurDate(newDate: Date): void {
    this.curDate = newDate;
  }

  @action private setJwtExpiredLeft(): void {
    this.jwtExpireLeft = this.calcJwtExpireLeft;
    if (this.jwtExpireLeft < 0) this.logOut();
  }

  @action initialize(): void {
    this.setJwtExpiredLeft();
    this.initialized = true;
  }

  constructor() {
    makeObservable(this);
    this.init();
  }

  @action private init() {
    log.log(`[AppStore] init`);
    console.log(`[AppStore] init values= `, this.user, this.userLogin);
    // this.user = '';
    // this.userLogin = { ...initialUserLogin };
    // setInterval(() => {
    //   this.setJwtExpiredLeft();
    // }, 1000);
  }

  //const { userParams } = useUserPermissions();
  @persist('list') @observable userParams: Array<Record<string, unknown>> = [];
  @action setUserParams(values: Array<Record<string, unknown>>): void {
    this.userParams = values;
  }
}

/** localStorage key for AppStore persistence (mobx-persist). */
export const PERSIST_APP_STORE_KEY = 'WasteMgmt-App-AppStore';

function persistAppStoreToStorage(store: AppStore): void {
  try {
    const data = {
      userLogin: store.userLogin,
      user: store.user,
      devMode: store.devMode,
      dbAlias: store.dbAlias,
      pageSize: store.pageSize,
      userParams: store.userParams,
    };
    localStorage.setItem(PERSIST_APP_STORE_KEY, JSON.stringify(data));
  } catch (e) {
    log.log('[AppStore] persist to localStorage failed', e);
  }
}
